import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Section } from "../Section";
import { track, trackConversion, trackFunnelStep } from "@/lib/analytics";
import {
  FOTO_ASSIGNMENT_LABELS,
  FOTO_ASSIGNMENT_VALUES,
  getFotoRecaptchaSiteKey,
  submitFotoQuote,
} from "@/lib/foto-quote.functions";
import { PHOTOGRAPHER } from "@/content/foto";
import { cn } from "@/lib/utils";

const SCRIPT_ID = "recaptcha-v3-script";

function useRecaptcha(siteKey: string | undefined) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return;
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => setReady(true));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => window.grecaptcha?.ready(() => setReady(true));
    document.head.appendChild(s);
  }, [siteKey]);
  return ready;
}

type Assignment = (typeof FOTO_ASSIGNMENT_VALUES)[number];

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-background/60 px-4 py-3 text-base outline-none transition focus:border-mint/60";
const labelClass =
  "mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground";

export function FotoQuoteForm({
  location = "foto",
  defaultAssignment = "vet-inte",
  defaultCity = "",
  title = "Få en offert på fotograferingen",
  lede = "Berätta kort vad ni behöver så återkommer vi med upplägg och pris inom en arbetsdag. Vi har inga fasta paketpriser — varje uppdrag offereras utifrån omfattning och användning.",
}: {
  location?: string;
  defaultAssignment?: Assignment;
  defaultCity?: string;
  title?: string;
  lede?: string;
} = {}) {
  const fetchKey = useServerFn(getFotoRecaptchaSiteKey);
  const submit = useServerFn(submitFotoQuote);
  const { data: keyData } = useQuery({
    queryKey: ["recaptcha-site-key"],
    queryFn: () => fetchKey(),
    staleTime: Infinity,
  });
  const siteKey = keyData?.siteKey;
  const ready = useRecaptcha(siteKey);

  const [assignment, setAssignment] = useState<Assignment>(defaultAssignment);
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const interacted = useRef(false);

  useEffect(() => {
    trackFunnelStep("contact_form_view", { location });
  }, [location]);

  function firstInteraction() {
    if (interacted.current) return;
    interacted.current = true;
    trackFunnelStep("contact_form_first_interaction", { location });
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    trackFunnelStep("contact_form_submit_attempt", { location, intent: assignment });
    if (!siteKey || !window.grecaptcha || !ready) {
      setError("Verifieringen är inte redo ännu. Försök igen om en stund.");
      track("contact_form_submit_error", { location, reason: "recaptcha_not_ready" });
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData(e.currentTarget);

      // Valfri referensbild → base64-bilaga i mejlet till oss.
      let attachment:
        | { filename: string; contentType: string; data: string }
        | null = null;
      const file = fd.get("reference");
      if (file instanceof File && file.size > 0) {
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];
        if (!allowed.includes(file.type)) {
          setStatus("error");
          setError("Bilagan måste vara JPG, PNG, WEBP, HEIC eller PDF.");
          return;
        }
        if (file.size > 3 * 1024 * 1024) {
          setStatus("error");
          setError("Bilagan får vara max 3 MB.");
          return;
        }
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = "";
        for (let i = 0; i < buf.length; i += 1) bin += String.fromCharCode(buf[i]!);
        attachment = {
          filename: file.name.slice(0, 120),
          contentType: file.type,
          data: btoa(bin),
        };
      }

      const token = await window.grecaptcha.execute(siteKey, { action: "foto_quote" });
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      const res = await submit({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          company: String(fd.get("company") ?? ""),
          assignment,
          scope: String(fd.get("scope") ?? ""),
          city: String(fd.get("city") ?? ""),
          date: String(fd.get("date") ?? ""),
          message: String(fd.get("msg") ?? ""),
          attachment,
          source_path: typeof window !== "undefined" ? window.location.pathname : "",
          token,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
        },
      });
      if (res.ok) {
        setStatus("ok");
        formRef.current?.reset();
        setFileName(null);
        trackConversion({
          conversion_type: "contact_form_submit",
          location,
          label: "Foto quote form",
          intent: assignment,
        });
      } else {
        setStatus("error");
        setError(res.error ?? "Något gick fel. Försök igen.");
        track("contact_form_submit_error", { location, reason: "server_error" });
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError("Något gick fel. Försök igen.");
      track("contact_form_submit_error", { location, reason: "exception" });
    }
  }

  return (
    <Section id="offert" className="bg-surface/40">
      <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
        {/* Kontaktperson */}
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Offert</div>
          <h2 className="text-balance text-4xl font-semibold leading-[1.05] md:text-5xl">
            {title}
          </h2>
          <p className="mt-5 text-pretty text-muted-foreground md:text-lg">{lede}</p>

          <figure className="mt-9 overflow-hidden rounded-3xl border border-white/10 bg-surface/60">
            <figcaption className="p-6">
              <div className="text-lg font-semibold">{PHOTOGRAPHER.name}</div>
              <div className="text-sm text-mint">{PHOTOGRAPHER.role}</div>
              <p className="mt-3 text-sm text-muted-foreground">{PHOTOGRAPHER.bio}</p>
              <a
                href={`mailto:${PHOTOGRAPHER.email}`}
                className="mt-4 inline-block text-sm underline underline-offset-4 hover:text-mint"
                onClick={() => track("contact_method_clicked", { location, method: "email" })}
              >
                {PHOTOGRAPHER.email}
              </a>
              <p className="mt-4 border-t border-white/10 pt-4 text-sm text-muted-foreground">
                Vill ni hellre ringa, mejla eller besöka oss?{" "}
                <Link
                  to="/kontakt"
                  className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  onClick={() => track("contact_method_clicked", { location, method: "kontakt_page" })}
                >
                  Alla kontaktuppgifter finns på Kontakta oss
                </Link>
                .
              </p>
            </figcaption>
          </figure>
        </div>

        {/* Formulär */}
        <form data-track-form="foto-offert"
          ref={formRef}
          onSubmit={onSubmit}
          onFocus={firstInteraction}
          className="rounded-3xl border border-white/10 bg-background/40 p-6 md:p-8"
        >
          <fieldset className="mb-6">
            <legend className={labelClass}>Vad gäller det?</legend>
            <div className="flex flex-wrap gap-2">
              {FOTO_ASSIGNMENT_VALUES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setAssignment(v);
                    firstInteraction();
                  }}
                  aria-pressed={assignment === v}
                  className={cn(
                    "rounded-full border px-4 py-2 text-sm transition",
                    assignment === v
                      ? "border-mint/60 bg-mint/10 text-mint"
                      : "border-white/10 bg-surface/60 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {FOTO_ASSIGNMENT_LABELS[v]}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className={labelClass}>Namn</span>
              <input name="name" autoComplete="name" className={fieldClass} />
            </label>
            <label>
              <span className={labelClass}>Företag</span>
              <input name="company" autoComplete="organization" className={fieldClass} />
            </label>
            <label>
              <span className={labelClass}>E-post *</span>
              <input
                name="email"
                type="email"
                required
                autoComplete="email"
                className={fieldClass}
              />
            </label>
            <label>
              <span className={labelClass}>Telefon</span>
              <input name="phone" type="tel" autoComplete="tel" className={fieldClass} />
            </label>
            <label>
              <span className={labelClass}>Omfattning</span>
              <input
                name="scope"
                placeholder="T.ex. 12 porträtt eller en halvdag"
                className={fieldClass}
              />
            </label>
            <label>
              <span className={labelClass}>Ort</span>
              <input
                name="city"
                defaultValue={defaultCity}
                placeholder="Var ska vi fotografera?"
                className={fieldClass}
              />
            </label>
            <label className="md:col-span-2">
              <span className={labelClass}>När vill ni fotografera?</span>
              <input
                name="date"
                placeholder="Datum eller ungefärlig period"
                className={fieldClass}
              />
            </label>
            <label className="md:col-span-2">
              <span className={labelClass}>Berätta mer</span>
              <textarea
                name="msg"
                rows={4}
                placeholder="Vad ska bilderna användas till? Sajt, annons, LinkedIn, pressrum …"
                className={cn(fieldClass, "resize-y")}
              />
            </label>
            <label className="md:col-span-2">
              <span className={labelClass}>Referensbild eller brief (valfritt)</span>
              <input
                name="reference"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                onChange={(e) => {
                  firstInteraction();
                  setFileName(e.currentTarget.files?.[0]?.name ?? null);
                }}
                className={cn(
                  fieldClass,
                  "file:mr-4 file:rounded-full file:border-0 file:bg-mint/15 file:px-4 file:py-2 file:text-sm file:text-mint",
                )}
              />
              <span className="mt-1.5 block text-xs text-muted-foreground">
                {fileName
                  ? `Bifogas: ${fileName}`
                  : "Bifoga gärna en bild på lokalen, en moodboard eller en brief (max 3 MB)."}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={status === "sending"}
            className="mt-6 w-full rounded-full bg-mint px-6 py-4 text-base font-semibold text-background transition hover:opacity-90 disabled:opacity-60"
          >
            {status === "sending" ? "Skickar …" : "Begär offert"}
          </button>

          <p className="mt-4 text-xs text-muted-foreground">
            Vi svarar inom en arbetsdag. Offerten är kostnadsfri och utan förpliktelser. Din
            förfrågan går till vår huvudinkorg.{" "}
            <Link
              to="/kontakt"
              className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
              onClick={() => track("contact_method_clicked", { location, method: "kontakt_page_form" })}
            >
              Se telefon, adress och övriga kontaktvägar
            </Link>
            .
          </p>

          {status === "ok" && (
            <p role="status" className="mt-4 rounded-xl border border-mint/40 bg-mint/10 p-4 text-sm text-mint">
              Tack! Din förfrågan är mottagen — du får en bekräftelse på mejlen och svar inom en
              arbetsdag.
            </p>
          )}
          {error && (
            <p role="alert" className="mt-4 rounded-xl border border-peach/40 bg-peach/10 p-4 text-sm text-peach">
              {error}
            </p>
          )}
        </form>
      </div>
    </Section>
  );
}
